import { useFlags } from "launchdarkly-react-client-sdk";
import { Package, SelectedChecksType } from "../../pages/order/OrderContext";
import { Schemas } from "../../types";

import { array, boolean, object, string } from "yup";
import { useTranslation } from "react-i18next";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import { t } from "i18next";
import isEqual from "lodash/isEqual";
import map from "lodash/map";

export const hasMonthPrecision = (
  precision?: Schemas["DatePrecisionEnum"] | null
): boolean => precision === "month" || precision === "day";

export type VerificationType =
  | "EDUCATION_VERIFICATIONS_ALL"
  | "EDUCATION_VERIFICATIONS_ALL_POST_SECONDARY"
  | "EDUCATION_VERIFICATIONS_HIGHEST_LEVEL";

export type CVReviewChecks = Pick<
  SelectedChecksType,
  "key" | "check_arguments"
>;

export const getCvReviewChecksForExtraction = (
  checks: CVReviewChecks[]
): Schemas["CVReviewCheckItem"][] => {
  return checks
    .filter((check) =>
      [
        "EMPLOYMENT_VERIFICATIONS_1",
        "EDUCATION_VERIFICATIONS_1",
        "EXPERIENCE_VERIFICATIONS_1",
      ].includes(check.key)
    )
    .map(
      (check) =>
        ({
          name: check.key,
          include_cv_review: check.check_arguments?.include_cv_review === true,
        }) as Schemas["CVReviewCheckItem"]
    );
};

const isValidVerificationType = (
  type?: string | null
): type is VerificationType =>
  !!type &&
  [
    "EDUCATION_VERIFICATIONS_ALL",
    "EDUCATION_VERIFICATIONS_ALL_POST_SECONDARY",
    "EDUCATION_VERIFICATIONS_HIGHEST_LEVEL",
  ].includes(type);

export const useCanOrderCVReview = (input: {
  regularChecks: SelectedChecksType[];
  packageChecks: Package["checks"];
}): {
  canOrderCVReview: boolean;
  templateId?: string;
  verificationType?: VerificationType;
  checks: CVReviewChecks[];
} => {
  const { regularChecks, packageChecks } = input;
  const { releaseCvReview } = useFlags();
  const { t } = useTranslation();

  // Normalize checks ordered via regular flow or via a package
  const checks = isEmpty(regularChecks)
    ? packageChecks.map((check) => ({
        key: check.id,
        check_arguments: check.check_arguments ?? {},
      }))
    : regularChecks.map((check) => ({
        key: check.key,
        check_arguments: check.check_arguments ?? {},
      }));

  if (!releaseCvReview)
    return {
      canOrderCVReview: false,
      templateId: undefined,
      verificationType: undefined,
      checks,
    };

  const canOrderCVReview = checks.some(
    (check) => check.check_arguments?.include_cv_review === true
  );

  if (!canOrderCVReview) {
    return {
      canOrderCVReview: false,
      templateId: undefined,
      verificationType: undefined,
      checks,
    };
  }

  const checksWithTemplate = checks.filter((check) =>
    ["EMPLOYMENT_VERIFICATIONS_1", "EXPERIENCE_VERIFICATIONS_1"].includes(
      check.key
    )
  );
  const checksWithVerificationType = checks.filter((check) =>
    ["EDUCATION_VERIFICATIONS_1"].includes(check.key)
  );

  if (checksWithTemplate.length > 1) {
    throw new Error(
      t(
        "CV Review arguments are invalid: Experience Verifications and Employment Verifications are mutually exclusive"
      )
    );
  }

  const extractedTemplateId = isEmpty(checksWithTemplate)
    ? null
    : checksWithTemplate[0].check_arguments?.template_id;
  const templateId = isString(extractedTemplateId)
    ? extractedTemplateId
    : undefined;

  if (!templateId && !isEmpty(checksWithTemplate)) {
    throw new Error(
      t(
        "Experience Verifications and Employment Verifications require a Template ID."
      )
    );
  }

  const extractedVerificationType = isEmpty(checksWithVerificationType)
    ? null
    : checksWithVerificationType[0].check_arguments?.verification_type;
  const verificationType =
    extractedVerificationType &&
    isString(extractedVerificationType) &&
    isValidVerificationType(extractedVerificationType)
      ? extractedVerificationType
      : undefined;

  if (!verificationType && !isEmpty(checksWithVerificationType)) {
    throw new Error(t("Education Verifications require a Verification Type."));
  }

  return { canOrderCVReview, templateId, verificationType, checks };
};

export const datesChanged = (input: {
  prev?: Schemas["CVExtractionResult"];
  next: Schemas["CVExtractionResult"];
}): boolean => {
  const { prev, next } = input;

  if (!prev) return false;

  const getDates = (data: Schemas["CVExtractionResult"]) => [
    ...map(data.employments, ({ start_date, end_date, is_current }) => ({
      start_date,
      end_date,
      is_current,
    })),
    ...map(data.educations, ({ start_date, end_date, is_current }) => ({
      start_date,
      end_date,
      is_current,
    })),
  ];

  return !isEqual(getDates(prev), getDates(next));
};

export const cvEducationItemSchema = object({
  institution_name: string().trim().required(),
  designation: string().nullable().label(t("Designation")),
  subject: string().nullable().label(t("Subject / Field of study")),
  start_date: string().required(),
  end_date: string()
    .nullable()
    .test(
      "end-after-start",
      t("End year must be equal to or after start year"),
      function (end_date) {
        const { start_date, is_current } = this.parent;
        if (is_current || !end_date || !start_date) return true;
        return end_date.slice(0, 4) >= start_date.slice(0, 4);
      }
    ),
  is_current: boolean().default(false),
  is_post_secondary: boolean().default(false),
}).required();

export const cvEmploymentItemSchema = object({
  company_name: string().trim().required().label(t("Company name")),
  position: string().nullable().label(t("Position")),
  start_date: string().required(),
  end_date: string()
    .nullable()
    .test(
      "end-after-start",
      t("End date must be equal to or after start date"),
      function (end_date) {
        const { start_date, is_current } = this.parent;
        if (is_current || !end_date || !start_date) return true;
        return end_date.slice(0, 7) >= start_date.slice(0, 7);
      }
    ),
  is_current: boolean().default(false),
}).required();

export const cvReviewSchema = object({
  confirmed_data: object({
    employments: array(cvEmploymentItemSchema).required(),
    educations: array(cvEducationItemSchema).required(),
  }).required(),
});
